import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  deleteDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import styles from './CompanyDeleteModal.module.css';

const CompanyDeleteModal = ({
  isOpen,
  onClose,
  company,
  currentUser,
  showNotification,
  logCompanyActivity
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deletionStats, setDeletionStats] = useState({
    users: 0,
    assets: 0,
    activities: 0
  });

  const handleDelete = async () => {
    if (!company || !company.id) {
      showNotification('No company selected for deletion', 'error');
      return;
    }
    
    if (confirmText !== 'DELETE') {
      showNotification('Please type DELETE to confirm', 'error');
      return;
    }

    // Check if user has permission
    if (!currentUser || currentUser.role !== 'superadmin') {
      showNotification('Only super admins can delete companies', 'error');
      return;
    }

    setIsDeleting(true);

    try {
      await deleteCompanyAndAllData(company.id);
      showNotification(
        `Company "${company.name}" and all associated data deleted successfully! (${deletionStats.users} users, ${deletionStats.assets} assets)`, 
        'success'
      );
      onClose();
    } catch (error) {
      console.error('Error deleting company:', error);
      showNotification(`Failed to delete company: ${error.message}`, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteCompanyAndAllData = async (companyId) => {
    const batch = writeBatch(db);
    let stats = { users: 0, assets: 0, activities: 0 };

    try {
      // 1. Get and prepare to delete all users associated with this company
      const usersQuery = query(
        collection(db, 'users'),
        where('companyId', '==', companyId)
      );
      const usersSnapshot = await getDocs(usersQuery);
      const companyUsers = [];

      usersSnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        companyUsers.push({
          id: userDoc.id,
          email: userData.email,
          uid: userData.uid || userDoc.id
        });
        // Add user document to batch for deletion
        batch.delete(userDoc.ref);
        stats.users++;
      });

      // 2. Get and prepare to delete all assets associated with this company
      const assetsQuery = query(
        collection(db, 'assets'),
        where('companyId', '==', companyId)
      );
      const assetsSnapshot = await getDocs(assetsQuery);

      assetsSnapshot.forEach((assetDoc) => {
        // Add asset document to batch for deletion
        batch.delete(assetDoc.ref);
        stats.assets++;
      });

      // 3. Get and prepare to delete all company activities
      const activitiesQuery = query(
        collection(db, 'companyActivities'),
        where('companyId', '==', companyId)
      );
      const activitiesSnapshot = await getDocs(activitiesQuery);

      activitiesSnapshot.forEach((activityDoc) => {
        // Add activity document to batch for deletion
        batch.delete(activityDoc.ref);
        stats.activities++;
      });

      // 4. Log the deletion activity BEFORE deleting (so it gets recorded)
      if (logCompanyActivity) {
        try {
          await logCompanyActivity(companyId, 'company_deleted', {
            companyName: company.name,
            companyId: company.companyId,
            deletedUsers: stats.users,
            deletedAssets: stats.assets,
            deletedActivities: stats.activities,
            deletedBy: currentUser?.email || 'unknown',
            timestamp: new Date().toISOString()
          });
        } catch (logError) {
          console.warn('Failed to log deletion activity:', logError);
          // Continue with deletion even if logging fails
        }
      }

      // 5. Add company document to batch for deletion
      batch.delete(doc(db, 'companies', companyId));

      // 6. Commit all Firestore deletions in one batch
      await batch.commit();

      // Update stats for UI feedback
      setDeletionStats(stats);

      // Note: Firebase Auth user deletion should be handled server-side
      // via Cloud Functions with Admin SDK for production use
      console.log(`Successfully deleted company data. Auth records for ${stats.users} users should be cleaned up server-side.`);

    } catch (error) {
      console.error('Company deletion error details:', error);
      throw new Error(`Company deletion failed: ${error.message}`);
    }
  };

  if (!isOpen || !company) return null;

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Delete Company</h3>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            disabled={isDeleting}
          >
            ×
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <div className={styles.warningIcon}>⚠️</div>
          
          <p className={styles.warningText}>
            You are about to permanently delete:
          </p>
          
          <div className={styles.deletionDetails}>
            <strong>Company:</strong> {company.name} ({company.companyId})
            <br />
            <strong>Associated Users:</strong> {company.userCount || 0} user(s)
            <br />
            <strong>Assets:</strong> {company.assets || 0} asset(s)
          </div>

          <div className={styles.consequencesList}>
            <p><strong>This action cannot be undone!</strong></p>
          </div>
          
          <div className={styles.confirmationSection}>
            <label htmlFor="confirmText">
              Type <strong>DELETE</strong> to confirm this action:
            </label>
            <input
              id="confirmText"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE here"
              className={styles.confirmInput}
              disabled={isDeleting}
            />
          </div>
        </div>
        
        <div className={styles.modalActions}>
          <button 
            onClick={onClose}
            className={styles.cancelButton}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button 
            onClick={handleDelete}
            className={styles.deleteButton}
            disabled={isDeleting || confirmText !== 'DELETE'}
          >
            {isDeleting ? 'Deleting...' : 'Delete Company'}
          </button>
        </div>
      </div>
    </div>
  );
};

CompanyDeleteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  company: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    companyId: PropTypes.string.isRequired,
    userCount: PropTypes.number,
    assets: PropTypes.number
  }),
  currentUser: PropTypes.object,
  showNotification: PropTypes.func.isRequired,
  logCompanyActivity: PropTypes.func
};

export default CompanyDeleteModal;
